import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { googleOAuthCallback } from '../store/actions/authActions';
import { loginSuccess, setGoogleConnectionStatus } from '../store/slices/authSlice';
import { apiInvoker } from '../lib/apiInvoker';
import { showError } from '../utils/toast';
import LoadingModal from '../components/common/LoadingModal';

const GoogleCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isProcessing, setIsProcessing] = useState(true);
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) {
      return;
    }

    const handleCallback = async () => {
      try {
        const token = searchParams.get('token');
        const userParam = searchParams.get('user');
        const success = searchParams.get('success');
        const error = searchParams.get('error');
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const existingToken = localStorage.getItem('token');

        if (success === 'true' && token) {
          hasProcessed.current = true;
          try {
            let user = null;
            if (userParam) {
              try {
                user = JSON.parse(decodeURIComponent(userParam));
              } catch (e) {
                console.error('Error parsing user data:', e);
              }
            }
            dispatch(loginSuccess({ token, user, rememberMe: true }));
            
            if (user && user.authProvider === 'google') {
              dispatch(setGoogleConnectionStatus({ 
                isConnected: true, 
                googleEmail: user.email || '' 
              }));
            }
            
            setIsProcessing(false);
            navigate('/dashboard', { replace: true });
            return;
          } catch (error) {
            console.error('Error processing token:', error);
            setIsProcessing(false);
            navigate('/signin', { replace: true });
            return;
          }
        }

        if (success === 'false' || error) {
          hasProcessed.current = true;
          setIsProcessing(false);
          const errorMessage = error ? decodeURIComponent(error) : 'Authentication failed';
          showError(errorMessage);
          navigate('/signin', { replace: true });
          return;
        }

        if (!code || !state) {
          hasProcessed.current = true;
          setIsProcessing(false);
          if (existingToken) {
            navigate('/dashboard', { replace: true });
          } else {
            navigate('/signin', { replace: true });
          }
          return;
        }

        hasProcessed.current = true;

        if (existingToken) {
          try {
            await apiInvoker.get(`/oauth/google/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`);
          } catch (error) {
            console.error('Error connecting Google:', error);
          }
          setIsProcessing(false);
          navigate('/dashboard', { replace: true });
        } else {
          await dispatch(googleOAuthCallback(code, state, navigate));
          setIsProcessing(false);
        }
      } catch (error) {
        setIsProcessing(false);
        const existingToken = localStorage.getItem('token');
        if (existingToken) {
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/signin', { replace: true });
        }
      }
    };

    handleCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LoadingModal
      isLoading={isProcessing}
      title="Completing Sign In"
      message="Please wait while we complete your Google sign in..."
    />
  );
};

export default GoogleCallback;

